/*
  NINI kereso - <nini-search> (snippets/nini-search.liquid).
  - Megnyitas: minden [data-nini-search-open] gomb/link (a headerben).
  - Gepeles kozben a hatterben kari le a talalatokat, oldaltoltes nelkul:
      Predictive Search API  /search/suggest?...&section_id=nini-search-results
      tartalek (ha a bolt nyelvere nincs predictive search): /search?...&section_id=...
    Csak Shopify sajat vegpontjai; a Search & Discovery szinonimai es
    kiemelesei ezekre automatikusan ervenyesek.
  - Elavult keresest megszakit (AbortController), ismetelt kifejezest cache-bol ad,
    idotullepes vagy halozati hiba eseten diszkret uzenetet mutat.
  - Legutobbi keresesek localStorage-ban (ha elerheto).
  Keszitette: Akos | branch: dev/akos | 2026-09-28
*/

(() => {
  const DEBOUNCE = 250;
  const TIMEOUT = 8000;
  const MIN_LENGTH = 2;
  const RECENT_KEY = 'nini-search:recent';
  const RECENT_MAX = 6;

  const storage = {
    read() {
      try {
        const value = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
        return Array.isArray(value) ? value : [];
      } catch (error) {
        return [];
      }
    },
    write(list) {
      try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
      } catch (error) {
        // Privat mod / tiltott tarolas: a legutobbi keresesek egyszeruen nem maradnak meg.
      }
    },
  };

  const supportsPredictiveSearch = () => {
    try {
      const features = document.getElementById('shopify-features');
      return features ? JSON.parse(features.textContent).predictiveSearch !== false : true;
    } catch (error) {
      return true;
    }
  };

  class NiniSearch extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.form = this.querySelector('.nini-search__form');
      this.input = this.querySelector('.nini-search__input');
      this.clearButton = this.querySelector('[data-action="clear"]');
      this.startElement = this.querySelector('[data-search-start]');
      this.resultsElement = this.querySelector('[data-search-results]');
      this.statusElement = this.querySelector('[data-search-status]');
      this.footerElement = this.querySelector('[data-search-footer]');
      this.recentGroup = this.querySelector('[data-recent-group]');
      this.recentList = this.querySelector('[data-recent-list]');

      this.cache = new Map();
      this.abortController = null;
      this.debounceTimer = null;
      this.lastTrigger = null;

      // A panel a body aljan legyen, hogy a sticky header ne vagja le.
      if (this.parentElement !== document.body) document.body.appendChild(this);

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-nini-search-open]');
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      });

      this.addEventListener('click', (event) => this._onClick(event));
      this.addEventListener('keydown', (event) => this._onKeydown(event));
      this.input.addEventListener('input', () => this._onInput());
      this.form.addEventListener('submit', () => this._remember(this.input.value));

      this._renderRecent();
    }

    get isOpen() {
      return !this.hidden;
    }

    open(trigger) {
      if (this.isOpen) return;
      this.lastTrigger = trigger || document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add('nini-search-open');
      document.querySelectorAll('[data-nini-search-open]').forEach((el) => el.setAttribute('aria-expanded', 'true'));

      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.input.focus({ preventScroll: true });
        this.input.select();
      });
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('nini-search-open');
      document.querySelectorAll('[data-nini-search-open]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
      this._abort();

      const finish = () => {
        this.hidden = true;
      };
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ? finish() : setTimeout(finish, 250);

      if (this.lastTrigger && document.contains(this.lastTrigger)) {
        this.lastTrigger.focus({ preventScroll: true });
      }
    }

    _onClick(event) {
      const action = event.target.closest('[data-action]');
      if (action) {
        switch (action.dataset.action) {
          case 'close':
            event.preventDefault();
            this.close();
            return;
          case 'clear':
            this.input.value = '';
            this.input.focus();
            this._onInput();
            return;
          case 'clear-recent':
            storage.write([]);
            this._renderRecent();
            return;
          case 'submit':
            this._submit();
            return;
        }
      }

      // Nepszeru / legutobbi kereses es javaslat: helyben lefuttatjuk, nem navigalunk.
      const term = event.target.closest('[data-search-term]');
      if (term) {
        event.preventDefault();
        this.input.value = term.dataset.searchTerm;
        this.input.focus();
        this._search(this.input.value.trim(), true);
        return;
      }

      // Termekre kattintaskor megjegyezzuk a kifejezest.
      if (event.target.closest('[data-search-product] a')) {
        this._remember(this.input.value);
      }
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Tab') {
        // Egyszeru fokuszcsapda a dialoguson belul.
        const focusable = Array.from(
          this.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"])')
        ).filter((el) => el.offsetParent !== null && !el.closest('[hidden]'));
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    _onInput() {
      const term = this.input.value.trim();
      this.clearButton.hidden = this.input.value === '';

      clearTimeout(this.debounceTimer);
      if (term.length < MIN_LENGTH) {
        this._abort();
        this._showStart();
        return;
      }

      this.debounceTimer = setTimeout(() => this._search(term), DEBOUNCE);
    }

    _submit() {
      if (this.input.value.trim() === '') {
        this.input.focus();
        return;
      }
      this._remember(this.input.value);
      this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit();
    }

    async _search(term, immediate = false) {
      clearTimeout(this.debounceTimer);
      this.clearButton.hidden = this.input.value === '';
      if (term.length < (immediate ? 1 : MIN_LENGTH)) return;

      const key = term.toLowerCase();
      if (this.cache.has(key)) {
        this._showResults(this.cache.get(key));
        return;
      }

      this._abort();
      const controller = new AbortController();
      this.abortController = controller;
      const timeout = setTimeout(() => controller.abort('timeout'), TIMEOUT);

      this.statusElement.hidden = true;
      this.resultsElement.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');

      try {
        const response = await fetch(this._buildUrl(term), {
          signal: controller.signal,
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = this._extract(await response.text());
        if (!html) throw new Error('Empty response');

        this.cache.set(key, html);
        if (this.input.value.trim().toLowerCase() === key) this._showResults(html);
      } catch (error) {
        // A felhasznalo altal megszakitott (ujabb gepeles, bezaras) kerest nem jelezzuk hibakent.
        if (controller.signal.aborted && controller.signal.reason !== 'timeout') return;
        if (this.input.value.trim().toLowerCase() === key) this._showError();
      } finally {
        clearTimeout(timeout);
        if (this.abortController === controller) {
          this.abortController = null;
          this.resultsElement.setAttribute('aria-busy', 'false');
          this.classList.remove('is-loading');
        }
      }
    }

    _buildUrl(term) {
      const limit = this.dataset.limit || '6';
      const params = new URLSearchParams();
      params.set('q', term);
      params.set('section_id', 'nini-search-results');

      if (supportsPredictiveSearch()) {
        params.set('resources[type]', this.dataset.resourceTypes || 'product');
        params.set('resources[limit]', limit);
        params.set('resources[limit_scope]', 'each');
        params.set('resources[options][unavailable_products]', 'last');
        return `${this.dataset.predictiveUrl}?${params.toString()}`;
      }

      params.set('type', 'product');
      params.set('options[prefix]', 'last');
      return `${this.dataset.searchUrl}?${params.toString()}`;
    }

    // A Section Rendering API a szekciot egy #shopify-section-... wrapperben adja vissza.
    _extract(text) {
      const template = document.createElement('template');
      template.innerHTML = text;
      const results = template.content.querySelector('.nini-search-results');
      if (!results) return '';

      // Tartalek vegpontnal a limitet itt alkalmazzuk.
      const limit = parseInt(this.dataset.limit || '6', 10);
      results.querySelectorAll('[data-search-product]').forEach((item, index) => {
        if (index >= limit) item.remove();
      });
      return results.outerHTML;
    }

    _abort() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.resultsElement.setAttribute('aria-busy', 'false');
      this.classList.remove('is-loading');
    }

    _showStart() {
      this.startElement.hidden = false;
      this.resultsElement.hidden = true;
      this.statusElement.hidden = true;
      this.footerElement.hidden = true;
      this._renderRecent();
    }

    _showResults(html) {
      this.resultsElement.innerHTML = html;
      const hasResults = this.resultsElement.querySelector('[data-has-results="true"]') !== null;

      this.statusElement.hidden = true;
      this.resultsElement.hidden = false;
      this.footerElement.hidden = !hasResults;
      // Talalat nelkul a nepszeru keresesek maradjanak lathatok, legyen hova tovabblepni.
      this.startElement.hidden = hasResults;
    }

    _showError() {
      this.statusElement.hidden = false;
      this.footerElement.hidden = false;
    }

    _remember(value) {
      if (!this.hasAttribute('data-recent')) return;
      const term = (value || '').trim();
      if (term.length < MIN_LENGTH) return;

      const list = storage.read().filter((item) => item.toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      storage.write(list.slice(0, RECENT_MAX));
    }

    _renderRecent() {
      if (!this.recentGroup || !this.recentList) return;
      const list = storage.read();
      this.recentGroup.hidden = list.length === 0;
      this.recentList.replaceChildren(
        ...list.map((term) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.className = 'nini-search__chip nini-search__chip--recent heading heading--small';
          link.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(term)}&type=product`;
          link.dataset.searchTerm = term;
          link.textContent = term;
          item.appendChild(link);
          return item;
        })
      );
    }
  }

  if (!customElements.get('nini-search')) customElements.define('nini-search', NiniSearch);
})();
